import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./lib/auth";
import { Layout } from "./components/layout/Layout";
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import Calculator from "./pages/Calculator";
import Contact from "./pages/Contact";
import SelfAssessment from "./pages/SelfAssessment";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Checkout from "./pages/Checkout";
import CheckoutConfirmation from "./pages/CheckoutConfirmation";
import Dashboard from "./pages/Dashboard";
import AdminPanel from "./pages/AdminPanel";
import KnowledgeHub from "./pages/KnowledgeHub";
import "./App.css";

// Protected Route wrapper
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/app" replace />;
  }

  return children;
};

// Public Route wrapper (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={isAdmin ? "/admin" : "/app"} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Marketing Pages */}
      <Route
        path="/"
        element={
          <Layout>
            <Home />
          </Layout>
        }
      />
      <Route
        path="/about"
        element={
          <Layout>
            <About />
          </Layout>
        }
      />
      <Route
        path="/services"
        element={
          <Layout>
            <Services />
          </Layout>
        }
      />
      <Route
        path="/calculator"
        element={
          <Layout>
            <Calculator />
          </Layout>
        }
      />
      <Route
        path="/contact"
        element={
          <Layout>
            <Contact />
          </Layout>
        }
      />
      <Route
        path="/self-assessment"
        element={
          <Layout>
            <SelfAssessment />
          </Layout>
        }
      />

      {/* Auth Pages */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Layout showFooter={false}>
              <Login />
            </Layout>
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Layout showFooter={false}>
              <Register />
            </Layout>
          </PublicRoute>
        }
      />

      {/* Checkout Flow */}
      <Route
        path="/checkout"
        element={
          <Layout showFooter={false}>
            <Checkout />
          </Layout>
        }
      />
      <Route
        path="/checkout/confirmation"
        element={
          <ProtectedRoute>
            <Layout showFooter={false}>
              <CheckoutConfirmation />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Producer Dashboard */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Knowledge Hub - Public route with internal access control */}
      <Route
        path="/knowledge"
        element={
          <Layout>
            <KnowledgeHub />
          </Layout>
        }
      />
      
      {/* 301 Redirect: /knowledge-hub → /knowledge (SEO - preserve old links) */}
      <Route
        path="/knowledge-hub"
        element={<Navigate to="/knowledge" replace />}
      />

      {/* Admin Panel */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <AdminPanel />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
